import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaMagic, FaPaperPlane, FaPlus, FaTimes } from 'react-icons/fa';
import CopilotHighlight from './CopilotHighlight';
import './Copilot.css';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

// Client-side allowlist mirroring the backend page catalog: the panel only
// ever navigates to known app routes, whatever the API returns.
const ALLOWED_ROUTES = [
  /^\/dashboard$/,
  /^\/tickets$/,
  /^\/tickets\/archive$/,
  /^\/tickets\/[a-f0-9]{24}\/messages$/i,
  /^\/ai-assistants$/,
  /^\/knowledge-board$/,
  /^\/carriers-instructions(\/[a-f0-9]{24})?$/i,
  /^\/clients-instructions(\/[a-f0-9]{24})?$/i,
  /^\/clients$/,
  /^\/connectors$/,
  /^\/parcel-monitor$/,
  /^\/voice-agents$/,
  /^\/voice-numbers$/,
  /^\/warehouses-instructions$/,
  /^\/ai-automation$/,
  /^\/router-agent$/,
  /^\/openapi-schemes$/,
  /^\/statistics$/,
  /^\/refunds$/,
  /^\/parameters$/,
  /^\/tracking-settings$/,
  /^\/followup-settings$/,
  /^\/status-instructions$/,
  /^\/notifications-settings$/,
  /^\/chatbot-settings$/,
  /^\/ticket-form-settings$/,
  /^\/voice-tickets$/,
  /^\/create-agent$/,
  /^\/catalogue$/,
  /^\/catalogue\/products\/[a-f0-9]{24}$/i,
  /^\/catalogue\/connections$/,
  /^\/catalogue\/assistant$/,
];

const isAllowedPath = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return false;
  const pathname = path.split('?')[0];
  return ALLOWED_ROUTES.some((pattern) => pattern.test(pathname));
};

// In-app copilot: chat panel + floating button, admin only (gated in Layout).
// Executes the backend's client actions (navigate / highlight / result cards)
// and renders write proposals as confirm/cancel cards — nothing is written
// without an explicit click on Confirm.
const CopilotPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [highlightAnchor, setHighlightAnchor] = useState(null);

  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [open, messages, pending, sending]);

  useEffect(() => {
    if (!open || loaded) return;
    axios
      .get('/api/copilot/conversation', { headers: authHeaders() })
      .then((response) => {
        setMessages(response.data.messages || []);
        setPending(response.data.pending_action || null);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, loaded]);

  const runActions = useCallback(
    (actions) => {
      (actions || []).forEach((action) => {
        if (action.type === 'navigate' && isAllowedPath(action.path)) {
          navigate(action.path);
        } else if (action.type === 'highlight' && /^[a-z0-9-]+$/.test(action.anchor || '')) {
          setHighlightAnchor(action.anchor);
        }
        // 'card' actions are rendered inline with the message, not executed.
      });
    },
    [navigate]
  );

  const applyTurn = useCallback(
    (data) => {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.reply, actions: data.actions || [] },
      ]);
      setPending(data.pending_action || null);
      runActions(data.actions);
    },
    [runActions]
  );

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setErrorText('');
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setPending(null);
    setSending(true);
    try {
      const response = await axios.post(
        '/api/copilot/message',
        { message: text, page: location.pathname },
        { headers: authHeaders() }
      );
      applyTurn(response.data);
    } catch (error) {
      setErrorText(t('copilot.error', 'The copilot is unavailable right now, please retry.'));
    } finally {
      setSending(false);
    }
  };

  const confirmPending = async (approved) => {
    if (confirming) return;
    setErrorText('');
    setConfirming(true);
    setPending(null);
    try {
      const response = await axios.post(
        '/api/copilot/confirm',
        { approved },
        { headers: authHeaders() }
      );
      applyTurn(response.data);
    } catch (error) {
      setErrorText(t('copilot.error', 'The copilot is unavailable right now, please retry.'));
    } finally {
      setConfirming(false);
    }
  };

  const resetConversation = async () => {
    try {
      await axios.delete('/api/copilot/conversation', { headers: authHeaders() });
    } catch (error) {
      /* a failed reset only means the old history reappears next open */
    }
    setMessages([]);
    setPending(null);
    setErrorText('');
  };

  const onInputKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const renderCards = (actions) =>
    (actions || [])
      .filter((action) => action.type === 'card')
      .map((card, index) => (
        <button
          key={index}
          type="button"
          className="copilot-card"
          onClick={() => isAllowedPath(card.path) && navigate(card.path)}
        >
          {card.title}
        </button>
      ));

  const busy = sending || confirming;

  return createPortal(
    <>
      {highlightAnchor && (
        <CopilotHighlight anchor={highlightAnchor} onDone={() => setHighlightAnchor(null)} />
      )}
      {!open && (
        <button
          type="button"
          className="copilot-fab"
          onClick={() => setOpen(true)}
          aria-label={t('copilot.open', 'Open the copilot')}
        >
          <FaMagic />
          <span>{t('copilot.title', 'Copilot')}</span>
        </button>
      )}
      {open && (
        <div className="copilot-panel" role="dialog" aria-label={t('copilot.title', 'Copilot')}>
          <div className="copilot-header">
            <span className="copilot-header-title">{t('copilot.title', 'Copilot')}</span>
            <div className="copilot-header-actions">
              <button
                type="button"
                className="copilot-icon-btn"
                onClick={resetConversation}
                title={t('copilot.newConversation', 'New conversation')}
              >
                <FaPlus />
              </button>
              <button
                type="button"
                className="copilot-icon-btn"
                onClick={() => setOpen(false)}
                title={t('copilot.close', 'Close')}
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="copilot-messages">
            {messages.length === 0 && !busy && (
              <div className="copilot-greeting">
                {t(
                  'copilot.greeting',
                  "Hi! I'm your Unanimity copilot. Ask me how to set up your account, find a ticket, or configure carriers, clients and automations — I can take you there and do it with you."
                )}
              </div>
            )}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`copilot-msg copilot-msg--${message.role === 'user' ? 'user' : 'assistant'}`}
              >
                <div className="copilot-msg-bubble">{message.content}</div>
                {renderCards(message.actions)}
              </div>
            ))}
            {pending && (
              <div className="copilot-pending">
                <div className="copilot-pending-title">
                  {t('copilot.pendingTitle', 'Action awaiting your confirmation')}
                </div>
                {(pending.items || []).map((item, index) => (
                  <div key={index} className="copilot-pending-item">
                    <div className="copilot-pending-label">{item.label}</div>
                    <ul className="copilot-pending-args">
                      {Object.entries(item.arguments || {}).map(([key, value]) => (
                        <li key={key}>
                          <span className="copilot-pending-key">{key}</span>{' '}
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className="copilot-pending-buttons">
                  <button
                    type="button"
                    className="u-btn u-btn--primary"
                    disabled={confirming}
                    onClick={() => confirmPending(true)}
                  >
                    {t('copilot.confirm', 'Confirm')}
                  </button>
                  <button
                    type="button"
                    className="u-btn u-btn--secondary"
                    disabled={confirming}
                    onClick={() => confirmPending(false)}
                  >
                    {t('copilot.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            )}
            {busy && (
              <div className="copilot-thinking">{t('copilot.thinking', 'Thinking…')}</div>
            )}
            {errorText && <div className="copilot-error">{errorText}</div>}
            <div ref={bottomRef} />
          </div>

          <div className="copilot-input-row">
            <textarea
              className="copilot-input"
              rows={2}
              value={input}
              disabled={busy}
              placeholder={t('copilot.placeholder', 'Ask anything about Unanimity…')}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={onInputKeyDown}
            />
            <button
              type="button"
              className="u-btn u-btn--primary copilot-send"
              disabled={busy || !input.trim()}
              onClick={sendMessage}
              aria-label={t('copilot.send', 'Send')}
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default CopilotPanel;
